import { useState, createContext, useContext, useMemo, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import moment from 'moment';

import { totalCountEvent } from 'services/generalServices';
import { stageUpdate } from 'services/TaskServices';
import { useToggle } from 'hooks/useToggle';
import { useToast } from 'context/ToastContext';
import { toastConstant } from 'constants/toastmessage';
import {
  createTaskStages,
  createTasks,
  deleteTask,
  deleteTaskStage,
  getTaskList,
  getTaskStages,
  updateTaskDetails,
  updateTaskStage,
} from 'services/CaseServices';
import { updateCaseStagePosition as updateTaskStagePosition } from 'services/Settings';
import { getLocalStorageParseItem, removeLocalStorageItem } from 'utils/localStorageUtils';
import { listUsers } from 'services/users/userServices';
import { convertToTimezoneFormat, setTimezoneWithoutAlteringTime } from 'utils/utility_functions/timezone';
import { UserDetailsContext } from 'context/userDetailsContext';
import { filterData_default } from 'constants/index';
import { getUserId } from 'utils/utils';

const TaskContext = createContext(() => {});
export const useTaskContext = () => useContext(TaskContext);

const TaskDataHandler = (props) => {
  const { isFromCase, caseDetails, isFromLead, isLeadClosed, isCaseClosed } = props;

  const isClosed = isLeadClosed || isCaseClosed;

  const { addToast } = useToast();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const userContext = useContext(UserDetailsContext);
  const [addTaskToggle, setAddTaskToggle] = useToggle(Boolean(location?.state?.openTask || queryParams.get('openTask')));
  const [manageColumnToggle, setManageColumnToggle] = useToggle(false);

  const [totalCount, setTotalCount] = useState(null);
  const [searchKeyWord, setSearchKeyWord] = useState('');
  const [selectedTask, setSelectedTask] = useState();
  const [taskStageState, setTaskStageState] = useState([]);
  const [selectedStageId, setSelectedStageId] = useState(null);
  const [taskData, setTaskData] = useState({});
  const [firmUserMap, setFirmUserMap] = useState({});
  const [isUserMapLoaded, setIsUserMapLoaded] = useState(false);
  const [firmUserIds, setFirmUserIds] = useState(
    isFromLead || isFromCase ? [] : userContext?.firmDetails?.user_preferences?.filters?.task?.user ?? [getUserId()]
  );
  const [filterData, setFilterData] = useState({
    // case: userContext?.firmDetails?.user_preferences?.filters?.task?.case || '',
    priority:
      isFromLead || isFromCase
        ? filterData_default.priority
        : userContext?.firmDetails?.user_preferences?.filters?.task?.priority || filterData_default.priority,
    date:
      isFromLead || isFromCase
        ? filterData_default.date
        : userContext?.firmDetails?.user_preferences?.filters?.task?.date || filterData_default.date,
    practice_area:
      isFromLead || isFromCase
        ? filterData_default.practice_area
        : userContext?.firmDetails?.user_preferences?.filters?.task?.practice_area || filterData_default.practice_area,
  });

  const updateUserIds = (ids) => {
    setFirmUserIds([...ids]);
  };

  const loadTaskList = () => {
    //Load task list
    totalCountEvent('task', caseDetails?.case_id).then((response) => {
      setTotalCount(response.data);
      loadTaskData({ pageLimit: response.data });
    });
  };

  const loadData = () => {
    loadTaskStages();
    totalCountEvent('task', caseDetails?.case_id).then((response) => {
      setTotalCount(response.data);
    });
    // Preload firm user map for mapping cognito/contact ids to firm_user_id
    totalCountEvent('user')
      .then((response) => {
        const obj = {
          status: 'ACTIVE',
          limit: response.data ?? 100,
          pagination_token: '',
          pagination_direction: 'forward',
          keyWord: '',
          next: '',
          is_include_contacts: false,
          is_detailed: false,
          is_confirmed_user: true,
          is_include_groups: false,
        };
        listUsers(obj)
          .then((res) => {
            const map = {};
            const arr = res?.data?.firm_users || [];
            arr.forEach((u) => {
              if (u?.firm_user_id) {
                // map common identifiers to firm_user_id
                if (u?.client_cognito_username) map[u.client_cognito_username] = u.firm_user_id;
                if (u?.contact_id) map[u.contact_id] = u.firm_user_id;
                map[u.firm_user_id] = u.firm_user_id;
                if (u?.user_id) map[u.user_id] = u.firm_user_id;
              }
            });
            setFirmUserMap(map);
            setIsUserMapLoaded(true);
          })
          .catch(() => {
            setIsUserMapLoaded(true);
          });
      })
      .catch(() => {});
  };

  const formatEndDate = (value) => {
    if (value === 'past-due') {
      return convertToTimezoneFormat(moment().endOf('day'), userContext.userDetails.timezone);
    } else if (value === '3-months') {
      return convertToTimezoneFormat(moment().add(90, 'days').endOf('day'), userContext.userDetails.timezone);
    } else {
      return '';
    }
  };
  const loadTaskData = ({
    caseId = filterData?.case_id?.id || caseDetails?.case_id,
    startDate = filterData?.date === '3-months'
      ? convertToTimezoneFormat(moment().subtract(90, 'days').startOf('day'), userContext.userDetails.timezone)
      : undefined,
    end_date = formatEndDate(filterData?.date),
    priority = filterData?.priority,
    practice_area = filterData?.practice_area,
    isSelf,
    assigneeId,
    status,
    data = {
      firm_user: [...new Set(firmUserIds.flat())],
      all_users: false,
    },
    pageLimit = totalCount,
  }) => {
    if (!isUserMapLoaded) {
      return;
    }

    const start_Date = setTimezoneWithoutAlteringTime(startDate, userContext.userDetails.timezone);
    const endDate = setTimezoneWithoutAlteringTime(end_date, userContext.userDetails.timezone);
    // Map selected IDs (cognito/contact) to firm_user_id using available taskData user list if present
    const selectedFlat = [...new Set((firmUserIds || []).flat())];
    const firm_user_ids = Array.from(
      new Set(
        selectedFlat
          .map((id) => {
            const mappedId = firmUserMap[id];
            return mappedId || (Object.values(firmUserMap).includes(id) ? id : null);
          })
          .filter((id) => id !== null && id !== undefined)
      )
    );

    const finalFirmUserIds = firm_user_ids.length > 0 ? firm_user_ids : [getUserId()];

    const payload = { ...data, firm_user_ids: finalFirmUserIds };
    getTaskList(caseId, start_Date, endDate, priority, isSelf, assigneeId, status, payload, pageLimit, '', null, practice_area)
      .then((response) => setTaskData(response.data))
      .catch((error) => console.error(error));
  };

  // Load Task Stages
  const loadTaskStages = () => {
    getTaskStages()
      .then((response) => setTaskStageState(response.data))
      .catch((error) => {
        addToast(false, toastConstant.toasterType.ERROR, toastConstant.api.FAILED, error.message || error);
      });
  };

  const createStage = (data) => {
    createTaskStages(data)
      .then(() => {
        loadTaskStages();
        loadTaskData({});
        addToast(false, toastConstant.toasterType.SUCCESS, toastConstant.api.SUCCESS, toastConstant.message.STAGE_CREATED_SUCCESSFULLY);
      })
      .catch((e) => {
        addToast(false, toastConstant.toasterType.ERROR, toastConstant.api.FAILED, e.response.data);
      });
  };

  const handleTaskComplete = (data) => {
    if (data?.sub_task?.length === 0) {
      const doneId = taskStageState?.find((stage) => stage?.task_stage_name === 'done')?.task_stage_id;
      stageUpdate(data.sk, doneId)
        .then(() => {
          loadTaskData({});
        })
        .catch((error) => {
          console.log(error);
        });
    }
    if (data?.sub_task?.length > 0) {
      handleSubTaskCheckBox(
        true,
        data.sub_task.map((item) => item?.sk),
        data
      );
    }
  };

  const handleCreateTasks = (payload) => {
    createTasks(payload)
      .then(() => {
        loadTaskList();
        addToast(false, toastConstant.toasterType.SUCCESS, toastConstant.api.SUCCESS, toastConstant.message.TASK_UPDATED_SUCCESSFULLY);
      })
      .catch(() => {
        addToast(false, toastConstant.toasterType.ERROR, toastConstant.api.FAILED, toastConstant.message.TASK_UPDATED_FAILED);
      });
  };

  const updateTask = (task_id, data) => {
    updateTaskDetails(task_id, null, data, false, isFromLead)
      .then(() => {
        loadTaskData({});
        addToast(false, toastConstant.toasterType.SUCCESS, toastConstant.api.SUCCESS, toastConstant.message.TASK_UPDATED_SUCCESSFULLY);
      })
      .catch(() => {
        addToast(false, toastConstant.toasterType.ERROR, toastConstant.api.FAILED, toastConstant.message.TASK_UPDATED_FAILED);
      });
  };

  const handleDeleteTask = (task) => {
    deleteTask(task.case_id, task.sk, task.pk, isFromLead ? isFromLead : task?.is_lead)
      .then(() => {
        addToast(false, toastConstant.toasterType.SUCCESS, toastConstant.api.SUCCESS, toastConstant.message.TASK_DELETED_SUCCESSFULLY);
        loadTaskList();
      })
      .catch(() => {
        addToast(false, toastConstant.toasterType.ERROR, toastConstant.api.FAILED, toastConstant.message.TASK_DELETE_FAILED);
      });
  };

  const handleSubTaskCheckBox = (checkValue, sub_sk, taskData, subTaskIndex) => {
    try {
      let data = JSON.parse(JSON.stringify(taskData));
      data.old_task_list = [...taskData.sub_task];

      if (sub_sk && sub_sk.length > 0) {
        let subSk = Array.isArray(sub_sk) ? [...sub_sk] : [sub_sk];
        data.sub_task.forEach((val) => {
          if (subSk.includes(val.sk)) {
            val.sub_task_status = checkValue;
          }
        });
      } else if (subTaskIndex !== undefined && subTaskIndex !== null) {
        if (data.sub_task[subTaskIndex]) {
          data.sub_task[subTaskIndex].sub_task_status = checkValue;
        }
      }

      data.old_task_list.forEach((item) => {
        item.sk = 'random';
      });

      let totoalSubTaskCompleted = data?.sub_task?.filter((item) => item?.sub_task_status === true);
      if (totoalSubTaskCompleted?.length === 0) {
        const toDoID = taskStageState?.find((stage) => stage?.task_stage_name === 'to_do')?.task_stage_id;
        data = {
          ...data,
          task_stage: data?.dynamic_task_stage || toDoID,
        };
      } else if (totoalSubTaskCompleted?.length < data?.sub_task?.length) {
        const inProgressID = taskStageState?.find((stage) => stage?.task_stage_name === 'in_progress')?.task_stage_id;
        data = { ...data, task_stage: inProgressID };
      } else if (totoalSubTaskCompleted?.length === data?.sub_task?.length) {
        const doneID = taskStageState?.find((stage) => stage?.task_stage_name === 'done')?.task_stage_id;
        data = { ...data, task_stage: doneID };
      }

      updateTaskDetails(taskData?.task_id, null, data)
        .then(() => {
          loadTaskData({});
          addToast(false, toastConstant.toasterType.SUCCESS, toastConstant.api.SUCCESS, toastConstant.message.TASK_UPDATED_SUCCESSFULLY);
        })
        .catch(() => {
          addToast(false, toastConstant.toasterType.ERROR, toastConstant.api.FAILED, toastConstant.message.TASK_UPDATED_FAILED);
        });
    } catch (err) {
      addToast(false, toastConstant.toasterType.ERROR, toastConstant.api.FAILED, toastConstant.message.TASK_UPDATED_FAILED);
    }
  };

  const updateStagePosition = (columnOrder) => {
    let stageData = Array.isArray(taskStageState) && taskStageState;
    if (stageData) {
      const updatedStage = stageData.map((item) => {
        if (item.task_stage_name !== 'done') {
          const index = columnOrder.indexOf(item.task_stage_name);
          return { ...item, position: index + 1 };
        }
        return item;
      });
      updateTaskStagePosition({ updated_positions: updatedStage }).catch(() => {
        loadTaskStages();
        addToast(false, toastConstant.toasterType.ERROR, toastConstant.api.FAILED, toastConstant.message.STAGE_UPDATED_FAILED);
      });
    }
  };

  const updateStageData = (id, data) => {
    let stageData = Array.isArray(taskStageState) && taskStageState?.find((v) => v.task_stage_name === id);
    if (stageData) {
      updateTaskStage(stageData?.pk, stageData?.sk, { ...stageData, ...data })
        .then(() => {
          loadTaskStages();
          loadTaskData({});
          addToast(false, toastConstant.toasterType.SUCCESS, toastConstant.api.SUCCESS, toastConstant.message.STAGE_UPDATED_SUCCESSFULLY);
        })
        .catch(() => {
          addToast(false, toastConstant.toasterType.ERROR, toastConstant.api.FAILED, toastConstant.message.STAGE_UPDATED_FAILED);
        });
    }
  };

  const deleteStage = (id) => {
    let stageData = Array.isArray(taskStageState) && taskStageState?.find((v) => v.task_stage_name === id);
    if (stageData) {
      deleteTaskStage(stageData?.pk, stageData?.sk)
        .then(() => {
          loadTaskData({});
          addToast(false, toastConstant.toasterType.SUCCESS, toastConstant.api.SUCCESS, toastConstant.message.STAGE_DELETE_SUCCESSFULLY);
        })
        .catch(() => addToast(false, toastConstant.toasterType.ERROR, toastConstant.api.FAILED, toastConstant.message.STAGE_DELETE_FAILED));
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    if (isUserMapLoaded && totalCount !== null) {
      loadTaskData({ pageLimit: totalCount });
    }
  }, [isUserMapLoaded, totalCount]);

  useEffect(() => {
    setAddTaskToggle(Boolean(location?.state?.openTask || queryParams.get('openTask')));
  }, [location]);

  useEffect(() => {
    const queryParams = new URLSearchParams(location.search);
    const task_id = queryParams.get('id');
    let selectedTaskData =
      getLocalStorageParseItem('stateData')?.viewTask || location?.state?.viewTask || (task_id ? { task_id, assign_to: [] } : null);

    if (selectedTaskData) {
      setSelectedTask(selectedTaskData);
      removeLocalStorageItem('stateData');
    }
  }, [window?.localStorage, location]); // for global search result on new tab

  const contextPayload = useMemo(
    () => ({
      userContext,
      taskData,
      totalCount,
      searchKeyWord,
      addTaskToggle,
      setAddTaskToggle,
      manageColumnToggle,
      setManageColumnToggle,
      selectedTask,
      taskStageState,
      selectedStageId,
      isFromCase,
      isFromLead,
      isClosed,
      caseDetails,
      filterData,
      setFilterData,
      loadTaskData,
      setSelectedStageId,
      createStage,
      handleDeleteTask,
      setSelectedTask,
      deleteStage,
      updateStageData,
      updateStagePosition,
      handleSubTaskCheckBox,
      updateTask,
      setSearchKeyWord,
      handleTaskComplete,
      loadTaskList,
      handleCreateTasks,
      updateUserIds,
    }),
    [
      userContext,
      isFromCase,
      caseDetails,
      taskData,
      taskStageState,
      totalCount,
      searchKeyWord,
      addTaskToggle,
      manageColumnToggle,
      selectedStageId,
      isFromLead,
      isClosed,
      filterData,
      updateStagePosition,
      deleteStage,
      updateStageData,
    ] //eslint-disable-line
  );

  return <TaskContext.Provider value={contextPayload} {...props} />;
};

export default TaskDataHandler;
